import { FormEvent, useEffect, useId, useRef, useState } from "react";
import Button from "../ui/button/Button";
import {
  buildingManagerService,
  PropertyManagerApiError,
  PropertyManagerCandidate,
  PropertyManagerMutationResult,
} from "../../services/buildingManagerService";

interface Props {
  buildingId: number;
  buildingName: string;
  isOpen: boolean;
  onClose: () => void;
  onCompleted: (result: PropertyManagerMutationResult) => void;
}

const inputClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AddPropertyManagerDialog({
  buildingId,
  buildingName,
  isOpen,
  onClose,
  onCompleted,
}: Props) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const submittingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<"search" | "create">("search");
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<PropertyManagerCandidate[]>([]);
  const [exactEmailConflict, setExactEmailConflict] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  submittingRef.current = isSubmitting;
  onCloseRef.current = onClose;

  const close = () => {
    if (!isSubmitting) onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    setStep("search");
    setQuery("");
    setMatches([]);
    setExactEmailConflict(false);
    setHasSearched(false);
    setError(null);
    setName("");
    setEmail("");
    setFieldErrors({});
    window.setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (!submittingRef.current) onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || step !== "search") return;
    const normalized = query.trim();
    if (normalized.length < 2) {
      setMatches([]);
      setHasSearched(false);
      setExactEmailConflict(false);
      setIsSearching(false);
      return;
    }

    setMatches([]);
    setHasSearched(false);
    setExactEmailConflict(false);

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setIsSearching(true);
      setError(null);
      try {
        const result = await buildingManagerService.searchCandidates(
          buildingId,
          normalized,
          controller.signal,
        );
        setMatches(result.matches);
        setExactEmailConflict(result.exact_email_conflict);
        setHasSearched(true);
      } catch (searchError) {
        if (!controller.signal.aborted) {
          setError(
            searchError instanceof Error
              ? searchError.message
              : "Search is temporarily unavailable.",
          );
          setHasSearched(true);
        }
      } finally {
        if (!controller.signal.aborted) setIsSearching(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [buildingId, isOpen, query, step]);

  if (!isOpen) return null;

  const hasEligibleCandidate = matches.some(
    (candidate) =>
      candidate.account_status === "active" && candidate.membership_status !== "active",
  );

  const beginCreate = () => {
    const normalized = query.trim();
    setName(emailPattern.test(normalized) ? "" : normalized);
    setEmail(emailPattern.test(normalized) ? normalized : "");
    setFieldErrors({});
    setError(null);
    setStep("create");
  };

  const finish = (result: PropertyManagerMutationResult) => {
    onCompleted(result);
  };

  const attach = async (candidate: PropertyManagerCandidate) => {
    if (candidate.membership_status === "active") return;
    setIsSubmitting(true);
    setError(null);
    try {
      finish(await buildingManagerService.attachExisting(buildingId, candidate.id));
    } catch (attachError) {
      setError(
        attachError instanceof Error ? attachError.message : "Unable to add this account.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitCreate = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedName = name.trim();
    const normalizedEmail = email.trim();
    const nextErrors: Record<string, string> = {};
    if (!normalizedName) nextErrors.name = "Name is required.";
    if (!normalizedEmail) nextErrors.email = "Email is required.";
    else if (!emailPattern.test(normalizedEmail)) nextErrors.email = "Enter a valid email address.";
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setIsSubmitting(true);
    setError(null);
    try {
      finish(
        await buildingManagerService.createAndAttach(buildingId, {
          name: normalizedName,
          email: normalizedEmail,
        }),
      );
    } catch (createError) {
      if (createError instanceof PropertyManagerApiError) {
        if (createError.code === "EMAIL_IN_USE_BY_OTHER_ROLE") {
          setFieldErrors({ email: createError.message });
        } else if (createError.code === "EMAIL_ALREADY_EXISTS") {
          // A concurrent create may win after the initial search. Return to
          // search so the administrator can attach the now-existing account
          // instead of retrying a duplicate create.
          setQuery(normalizedEmail);
          setStep("search");
          setError("An account now uses this email. Select it from the search results.");
        } else if (createError.code === "VALIDATION_FAILED" && createError.errors) {
          setFieldErrors(
            Object.fromEntries(
              Object.entries(createError.errors).map(([key, messages]) => [key, messages[0]]),
            ),
          );
        } else {
          setError(createError.message);
        }
      } else {
        setError(createError instanceof Error ? createError.message : "Unable to create this account.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-999999 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(event) => event.target === event.currentTarget && close()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-theme-xl dark:bg-gray-900 sm:p-8"
      >
        <button
          type="button"
          aria-label="Close add property manager dialog"
          onClick={close}
          disabled={isSubmitting}
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:hover:bg-gray-800"
        >
          <span aria-hidden="true" className="text-2xl">×</span>
        </button>

        <div className="pr-12">
          <h2 id={titleId} className="text-xl font-semibold text-gray-800 dark:text-white/90">
            Add property manager
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{buildingName}</p>
        </div>

        {error && (
          <div role="alert" className="mt-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        )}

        {step === "search" ? (
          <div className="mt-6">
            <label htmlFor="property-manager-search" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Search by name or email
            </label>
            <div className="relative">
              <input
                ref={searchInputRef}
                id="property-manager-search"
                type="search"
                role="combobox"
                aria-expanded={hasSearched && matches.length > 0}
                aria-controls="property-manager-results"
                autoComplete="off"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Start typing a name or email"
                className={`${inputClass} pr-10`}
              />
              {isSearching && (
                <span className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" aria-label="Searching" />
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">Enter at least 2 characters.</p>

            <div id="property-manager-results" role="listbox" className="mt-5 space-y-2">
              {exactEmailConflict && (
                <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                  An account already uses this email with another role. It cannot be added as a property manager.
                </div>
              )}
              {matches.map((candidate) => (
                <div key={candidate.id} role="option" aria-selected="false" className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-gray-800 dark:text-white/90">{candidate.name}</p>
                    <p className="truncate text-sm text-gray-500 dark:text-gray-400">{candidate.email}</p>
                  </div>
                  <Button
                    size="sm"
                    variant={candidate.membership_status === "active" ? "outline" : "primary"}
                    disabled={isSubmitting || candidate.membership_status === "active" || candidate.account_status === "disabled"}
                    onClick={() => attach(candidate)}
                    className="shrink-0"
                  >
                    {candidate.account_status === "disabled"
                      ? "Account disabled"
                      : candidate.membership_status === "active"
                        ? "Already added"
                        : candidate.membership_status === "archived"
                          ? "Restore access"
                          : "Add"}
                  </Button>
                </div>
              ))}
            </div>

            {hasSearched && !isSearching && matches.length === 0 && !exactEmailConflict && (
              <p className="mt-5 rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500 dark:bg-white/[0.03] dark:text-gray-400">
                No existing property managers matched your search.
              </p>
            )}

            {hasSearched && !exactEmailConflict && !hasEligibleCandidate && (
              <button type="button" onClick={beginCreate} className="mt-5 w-full rounded-xl border border-dashed border-brand-300 px-4 py-4 text-sm font-medium text-brand-600 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-brand-700 dark:text-brand-400 dark:hover:bg-brand-500/10">
                Create new property manager
              </button>
            )}

            <div className="mt-6 flex justify-end">
              <Button variant="outline" onClick={close} disabled={isSubmitting}>Cancel</Button>
            </div>
          </div>
        ) : (
          <form className="mt-6" onSubmit={submitCreate} noValidate>
            <button type="button" onClick={() => setStep("search")} className="mb-5 text-sm font-medium text-brand-500 hover:text-brand-600 focus:outline-none focus:underline">
              ← Back to search
            </button>
            <div className="space-y-4">
              <div>
                <label htmlFor="property-manager-name" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Full name <span className="text-red-500">*</span></label>
                <input id="property-manager-name" autoFocus value={name} onChange={(event) => setName(event.target.value)} aria-invalid={Boolean(fieldErrors.name)} aria-describedby={fieldErrors.name ? "property-manager-name-error" : undefined} className={inputClass} placeholder="Jane Smith" />
                {fieldErrors.name && <p id="property-manager-name-error" className="mt-1.5 text-sm text-red-600">{fieldErrors.name}</p>}
              </div>
              <div>
                <label htmlFor="property-manager-email" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Email <span className="text-red-500">*</span></label>
                <input id="property-manager-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} aria-invalid={Boolean(fieldErrors.email)} aria-describedby={fieldErrors.email ? "property-manager-email-error" : undefined} className={inputClass} placeholder="jane@example.com" />
                {fieldErrors.email && <p id="property-manager-email-error" className="mt-1.5 text-sm text-red-600">{fieldErrors.email}</p>}
              </div>
            </div>
            <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={close} disabled={isSubmitting}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting}>{isSubmitting ? "Creating…" : "Create and add"}</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
